import { useIsFetching } from '@tanstack/react-query';
import { useMemo } from 'react';

import { AccountDocuments } from './_authenticated._main.company/AccountDocuments';
import allocabLogo from '../assets/images/allocab-logo.svg';
import { GradientButton } from '../components/button/GradientButton';
import { SecondaryButton } from '../components/button/SecondaryButton';
import { useAuth } from '../hooks/useAuth';
import { useFetchAccountDocuments } from '../hooks/useFetchAccountDocument';
import { useFetchUserSubscription } from '../hooks/useFetchUserSubscription';
import useFinishSubscription from '../hooks/useFinishSubscription';
import { queryKeys } from '../services/datastore/queryKeys';
import { DEFAULT_CACHE_TIME } from '../services/datastore/react-query';
import { ToastManager } from '../services/toast/utils';
import { t } from '../services/translation/translation';
import { isDocumentItemComplete } from '../utils/document';

export const SubscriptionRoute = () => {
  const { logoutMutation } = useAuth();
  const { data } = useFetchAccountDocuments();
  const isFetchingUser = useIsFetching(queryKeys.user.me()) > 0;
  const { data: subscription, isFetching: isFetchingSubscription } = useFetchUserSubscription({
    staleTime: DEFAULT_CACHE_TIME,
  });
  const { mutate: finishSubscription, isLoading: isFinishingSubscription } = useFinishSubscription();

  const isValid = useMemo(() => data && data.items.every(item => isDocumentItemComplete(item)), [data]);

  const handleSubmit = () => {
    if (!isValid) {
      ToastManager.show({
        message: t('subscription-route.not-valid'),
        severity: 'warning',
      });
      return;
    }

    if (subscription) {
      finishSubscription(subscription.id);
    }
  };

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto min-h-screen">
        <div className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
          <img className="h-10 w-auto" src={allocabLogo} alt="allocab" />
        </div>
        <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
              {t('subscription-route.title')}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">{t('subscription-route.description')}</p>
            <AccountDocuments compressed />
            <div className="space-y-2">
              <GradientButton
                className="w-full"
                text={t('common.continue')}
                onClick={handleSubmit}
                isDisabled={isFetchingSubscription || logoutMutation.isLoading}
                isLoading={isFinishingSubscription || isFetchingUser}
              />
              <SecondaryButton
                text={t('common.logout')}
                className="w-full"
                onClick={() => logoutMutation.mutate()}
                isLoading={logoutMutation.isLoading}
                isDisabled={isFinishingSubscription}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
